import { AreaSwitchBandPathSchema } from '~/types/areaSwitchBand';
import type { AreaSwitchBandState } from '~/types/areaSwitchBand';
import type { SectionId } from '~/types/sectionId';
import { isCheckoutPath } from '~/utils/isCheckoutPath';

export interface AreaSwitchBandPageView {
    readonly area: SectionId;
    /** The locale segment of the page, for example `de-de`. */
    readonly localeSegment: string;
    /** The path with query. The hash is not part of it. */
    readonly fullPath: string;
}

/**
 * Returns the path with query when it fits in the cookie. Otherwise, it returns only the path. When the
 * path does not fit too, it returns an empty string. The back link then goes to the start page of the area.
 */
export function toStoredPath(fullPath: string): string {
    const pathWithQuery = fullPath.split('#', 1)[0] ?? '';
    if (AreaSwitchBandPathSchema.safeParse(pathWithQuery).success) return pathWithQuery;

    const pathOnly = pathWithQuery.split('?', 1)[0] ?? '';
    return AreaSwitchBandPathSchema.safeParse(pathOnly).success ? pathOnly : '';
}

/** The band after a page view: it starts only when the user switches to a different area in the same locale. */
function resolveBand(
    state: AreaSwitchBandState | null,
    current: AreaSwitchBandState['current']
): AreaSwitchBandState['band'] {
    if (state?.current.localeSegment !== current.localeSegment) return null;
    if (state.current.area === current.area) return state.band;

    return { area: state.current.area, path: state.current.path };
}

/**
 * Records a page view. Processing the same page view again gives the same state: on the first page load,
 * the server and the browser both record it.
 */
export function applyPageView(
    state: AreaSwitchBandState | null,
    pageView: AreaSwitchBandPageView
): AreaSwitchBandState | null {
    if (isCheckoutPath(pageView.fullPath)) return state;

    const current = {
        area: pageView.area,
        localeSegment: pageView.localeSegment,
        path: toStoredPath(pageView.fullPath)
    };

    return { current, band: resolveBand(state, current) };
}

/**
 * The user clicked the back link. The back target is now the current page, and no band is visible there.
 * `backUrl` must be the URL that `resolveAreaSwitchBandBackUrl` gave.
 */
export function applyBackLinkClick(state: AreaSwitchBandState | null, backUrl: string): AreaSwitchBandState | null {
    if (!state?.band) return state;

    return {
        current: { area: state.band.area, localeSegment: state.current.localeSegment, path: toStoredPath(backUrl) },
        band: null
    };
}

export function applyCloseClick(state: AreaSwitchBandState | null): AreaSwitchBandState | null {
    if (!state?.band) return state;

    return { current: state.current, band: null };
}
